import * as R from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'

import FundraisingRank from '../../../components/FundraisingRank'
import Sorter from '../../../components/Sorter'
import { DEFAULT_PROFILE_URL } from '../../../constants'
import { withTranslation } from '../../../i18n'
import CreateFundraisingEventSvg from '../../../public/static/images/icon-fundraising-publish-success.svg'
import Rank1 from '../../../public/static/images/ranking-icon-1.svg'
import Rank2 from '../../../public/static/images/ranking-icon-2.svg'
import Rank3 from '../../../public/static/images/ranking-icon-3.svg'
import { getProjectFundraisingEventRankingRequestAsync } from '../../../redux/project/actions'
import { getColor, getFontSize } from '../../../utils/cssUtils'
import CardWrapper from '../../CardWrapper'

const StyledCardWrapper = styled(CardWrapper)`
  padding: 16px;
`
const TabWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`
const Tab = styled.div`
  color: ${getColor('grey')};
  font-size: ${getFontSize('body2')};
  font-weight: bold;
  margin-right: 24px;
  &:last-of-type {
    margin-right: 0;
  }
  cursor: ${({ isActive }) => (isActive ? 'default' : 'pointer')};
  ${({ isActive }) =>
    isActive &&
    css`
      color: ${getColor('black')};
      border-bottom: 2px solid ${getColor('black')};
    `};
`
const SorterWrapper = styled.div`
  display: flex;
  align-items: center;
`
const Headline = styled.div`
  color: ${getColor('black')};
  font-size: ${getFontSize('title')};
  font-weight: bold;
  margin-bottom: 16px;
`
const EmptyRanking = styled.div`
  padding: 72px 0;
  display: flex;
  justify-content: center;
  flex-flow: row wrap;
  > * {
    width: 100%;
  }
`
const EmptyRankingText = styled.div`
  color: ${getColor('grey')};
  font-size: ${getFontSize('body2')};
  text-align: center;
`

const RankingSorter = withTranslation('common')(
  ({ t, onSort, defaultSortBy }) => {
    const sorterOptions = useMemo(
      () => [
        { id: 'totalDonationAmount', label: t('project.donatedAmount') },
        { id: 'totalDonorCount', label: t('project.donorCount') }
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )

    return (
      <Sorter
        options={sorterOptions}
        defaultSortBy={defaultSortBy}
        onSort={onSort}
      />
    )
  }
)

const getRank = R.cond([
  [R.equals(1), R.always(<Rank1 />)],
  [R.equals(2), R.always(<Rank2 />)],
  [R.equals(3), R.always(<Rank3 />)],
  [R.T, R.identity]
])

const getAvatarUrl = creator =>
  R.when(
    R.either(R.isNil, R.isEmpty),
    R.always(DEFAULT_PROFILE_URL)
  )(R.prop('avatarUrl', creator))

const Ranking = withTranslation('common')(({ t, projectNo }) => {
  const dispatch = useDispatch()

  const rankingOpts = useSelector(
    R.path([
      'projectState',
      'entities',
      'projects',
      projectNo,
      'fundraisingEventRanking'
    ]),
    R.equals
  )

  const { displayCorporate, displayPersonal, ordering = [] } = rankingOpts ?? {}

  const ranking = useSelector(
    R.path(['projectState', 'entities', 'projects', projectNo, 'ranking']),
    R.equals
  )

  const [sortBy, setSortBy] = useState('totalDonationAmount')

  const groupsMap = {
    corporate: {
      key: 'corporate',
      name: t('personalFundraising.ranking.corporate')
    },
    personal: {
      key: 'personal',
      name: t('personalFundraising.ranking.personal')
    }
  }

  let groups = R.map(group => groupsMap[group], ordering)

  if (!displayCorporate) {
    groups = R.remove(
      R.findIndex(R.propEq('key', 'corporate'))(groups),
      1,
      groups
    )
  }

  if (!displayPersonal) {
    groups = R.remove(
      R.findIndex(R.propEq('key', 'personal'))(groups),
      1,
      groups
    )
  }

  const [tab, setTab] = useState(groups[0]?.key ?? 'corporate')

  const onSort = selected => {
    setSortBy(selected)
  }

  const handleTabClick = key => e => {
    setTab(key)
    setSortBy('totalDonationAmount')
  }

  useEffect(() => {
    if (displayCorporate || displayPersonal) {
      ;(async () => {
        try {
          await dispatch(
            getProjectFundraisingEventRankingRequestAsync(
              projectNo,
              tab,
              sortBy
            )
          )
        } catch (e) {
          console.log(e)
        }
      })()
    }
  }, [tab, sortBy])

  return displayCorporate || displayPersonal ? (
    <StyledCardWrapper>
      <Headline>{t('personalFundraising.ranking.headline')}</Headline>
      <TabWrapper>
        {groups.map(({ key, name }) => (
          <Tab key={key} isActive={tab === key} onClick={handleTabClick(key)}>
            {name}
          </Tab>
        ))}
      </TabWrapper>
      <SorterWrapper>
        <RankingSorter onSort={onSort} defaultSortBy={sortBy} />
      </SorterWrapper>
      {ranking &&
        (R.isEmpty(ranking) ? (
          <EmptyRanking>
            <CreateFundraisingEventSvg />
            <EmptyRankingText>
              {t('personalFundraising.ranking.createFundraisingEventNow')}
            </EmptyRankingText>
          </EmptyRanking>
        ) : (
          ranking.map(({ creator, donationStatistics }, i) => (
            <FundraisingRank
              creatorName={creator?.displayName}
              avatarUrl={getAvatarUrl(creator)}
              rank={getRank(i + 1)}
              totalDonorCount={donationStatistics?.totalDonorCount}
              totalDonationAmount={R.unless(
                R.isNil,
                R.divide(R.__, 100)
              )(donationStatistics?.totalDonationAmount)}
              key={i}
            />
          ))
        ))}
    </StyledCardWrapper>
  ) : null
})

export default Ranking
